import { useState } from "react";
import { useParams } from "wouter";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { LayoutDashboard, Users, FileText, DollarSign, Settings, Calendar, ClipboardList } from "lucide-react";
import ACADashboard from "./ACADashboard";
import ACAEmployees from "./ACAEmployees";
import ACAPolicies from "./ACAPolicies";
import ACAAffordability from "./ACAAffordability";
import ACASettings from "./ACASettings";
import ACAFormsTab from "./ACAFormsTab";
import ACAHistory from "./ACAHistory";

interface ACAModuleProps {
  clientId?: number;
}

export default function ACAModule({ clientId: propClientId }: ACAModuleProps) {
  // Broker routes mount this at /clients/:id/aca without props; the employer
  // route relies on the tabs' session fallback.
  const params = useParams<{ id?: string }>();
  const routeClientId = params?.id ? Number(params.id) : undefined;
  const clientId = propClientId ?? (Number.isFinite(routeClientId) && routeClientId! > 0 ? routeClientId : undefined);
  const [activeTab, setActiveTab] = useState("dashboard");

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold">ACA Compliance</h1>
        <p className="text-muted-foreground">
          Manage Affordable Care Act reporting and compliance
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid grid-cols-7 w-full max-w-4xl">
          <TabsTrigger value="dashboard" className="flex items-center gap-2">
            <LayoutDashboard className="h-4 w-4" />
            Dashboard
          </TabsTrigger>
          <TabsTrigger value="employees" className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            Employees
          </TabsTrigger>
          <TabsTrigger value="policies" className="flex items-center gap-2">
            <ClipboardList className="h-4 w-4" />
            Policies
          </TabsTrigger>
          <TabsTrigger value="affordability" className="flex items-center gap-2">
            <DollarSign className="h-4 w-4" />
            Affordability
          </TabsTrigger>
          <TabsTrigger value="settings" className="flex items-center gap-2">
            <Settings className="h-4 w-4" />
            Settings
          </TabsTrigger>
          <TabsTrigger value="forms" className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Forms
          </TabsTrigger>
          <TabsTrigger value="history" className="flex items-center gap-2">
            <Calendar className="h-4 w-4" />
            History
          </TabsTrigger>
        </TabsList>

        <TabsContent value="dashboard">
          <ACADashboard clientId={clientId} />
        </TabsContent>

        <TabsContent value="employees">
          <ACAEmployees clientId={clientId} />
        </TabsContent>

        <TabsContent value="policies">
          <ACAPolicies clientId={clientId} />
        </TabsContent>

        <TabsContent value="affordability">
          <ACAAffordability clientId={clientId} />
        </TabsContent>

        <TabsContent value="settings">
          <ACASettings clientId={clientId} />
        </TabsContent>

        <TabsContent value="forms">
          <ACAFormsTab clientId={clientId} />
        </TabsContent>

        <TabsContent value="history">
          <ACAHistory clientId={clientId} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
